// React
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useHistory, useLocation } from "react-router-dom";

// Context
import ModalContext from "../../../../contexts/modal";
import AuthContext from "../../../../contexts/auth";

// Hooks
import { useIncidents } from "../../../../hooks/useIncidents";

// Layout
import {
  PageContainer,
  PageHeader,
  ContentCard,
} from "../../../../components/layout";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";

// Icon
import { LuAlertCircle, LuCalendar } from "react-icons/lu";
import { TbFileDescription, TbFlag3, TbListDetails, TbWorld } from "react-icons/tb";
import { FaChevronLeft, FaDoorOpen } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import { IoIosSave, IoMdContact, IoMdSettings } from "react-icons/io";

// Utility imports
import {
  getFlagPriorityIncident,
  getNameIncidentsPriority,
  getNameIncidentsStatus,
  getNameIncidentsTypes,
  getNameIncidentsOrigins,
  translate,
  getIconStatusIncident,
  getFormatDate,
  getColorPriorityIncident,
  getColorStatusIncident,
} from "../../../../utils/Common";

// Component imports
import TypeIncidentSelect from "../TypeIncidentSelect";
import PriorityIncidentList from "../PriorityIncidentList";
import StatusIncidentList from "../StatusIncidentList";
import OriginIncidentList from "../OriginIncidentList";
import LockersSearchList from "../../../../components/LockersSearchList";
import ClientsSearchList from "../../../../components/ClientsSearchList";
import Spinner from "../../../../components/Spinner";
import ShowTracking from "../../../../components/ShowTracking";
import ItemIncidents from "../../../../components/ShowTracking/ItemIncidents";
import { IoMailOutline, IoSettingsOutline } from "react-icons/io5";
import { PiLockers, PiPackageFill, PiUser } from "react-icons/pi";
import FileViewerAWS from "../../../../components/ui/FileViewerAWS";
import AssignedUserSelect from "../AssignedUserSelect";
import AvatarName from "../../../../components/ui/AvatarName";

import styles from "./IncidentDetail.module.css";
import { verifyAccessViewPermissions } from "../../../../config/permissions";

const statusColorToBadgeVariant = {
  "bg-blue-500": "info",
  "bg-yellow-500": "warning",
  "bg-orange-500": "warning",
  "bg-green-500": "success",
  "bg-gray-500": "neutral",
  "bg-red-500": "error",
  "bg-purple-500": "archived",
};

const priorityToBadgeVariant = {
  danger: "error",
  warning: "warning",
  primary: "info",
};

const priorityToAlertClass = {
  danger: styles.descriptionAlertDanger,
  warning: styles.descriptionAlertWarning,
  primary: styles.descriptionAlertPrimary,
};

const UpdateIncident = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { register, errors, reset, handleSubmit, setValue, setError, clearErrors, watch } = useForm();

  const { updateIncident, getDetailsIncident, getTrackingIncident, getViewUrlFile } =
    useIncidents();
  const modalContext = useContext(ModalContext);
  const userContext = useContext(AuthContext);

  const [detailsIncidents, setDetailsIncidents] = useState(null);  
  const [trackingIncidents, setTrackingIncidents] = useState(null);
  const [isSendingData, setIsSendingData] = useState(false);
  const [signedUrls, setSignedUrls] = useState([]);
  const [filesUrl, setFilesUrl] = useState([]);
  const [fetchIntervalId, setFetchIntervalId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState(null);
  const [isActiveUpdate, setIsActiveUpdate] = useState(true);
  const [editPlatform, setEditPlatform] = useState(null);
  const [editClient, setEditClient] = useState(null);
  // Copia local de los errores: react-hook-form v6 no re-renderiza bajo React.StrictMode
  // (marca el form como desmontado en el doble montaje de dev), así que sus errores no se pintaban
  const [localErrors, setLocalErrors] = useState({});
  const visibleErrors = { ...errors, ...localErrors };

  const submitUpdateIncident = async (data) => {
    
    try {
      if (!canUpdate) return;
      setLocalErrors({});
      setIsLoading(true);
      setIsActiveUpdate(!isActiveUpdate);

      if (!isActiveUpdate) {

        // Añadir validación de que no se puede actualizar el estatus de una incidencia a  en proceso, si no esta asignada a un técnico.
       if (data.statusIncident === "3" && !assignedUserId) {
          const technicianError = {
            type: "manual",
            message: translate("global.selectTechnicianRequired"),
          };
          setError("assignedUser", technicianError);
          setLocalErrors({ assignedUser: technicianError });
          scrollToField("assignedUser");
          setIsLoading(false);
          setIsActiveUpdate(false);
          return;
        } else {          
          clearErrors("assignedUser");
        }

        const controller = new AbortController();
        setIsSendingData(true);
        const result = await updateIncident(
          {
            ...data,
            plataforma: editPlatform ?? "",
            clientAsigned: editClient ?? "",
            incidentId: id,
            email: detailsIncidents?.Email,
          },
          controller.signal
        );
        // Recargar detalle y tracking explícitamente: con el batching de React 18 el
        // true/false de isSendingData se fusionaba y el effect no volvía a correr
        handleGetIncident();
        handleGetTrackingIncident();
        if (result) {
          if (result.code === 200) {
            if (result.payload) {
              modalContext.confirm(
                translate(
                  "dashboard.packages.loadpackage.formProvider.successTittle"
                ),
                <div>
                  <strong>
                    {" "}
                    {translate("global.incident")} # {result.payload.incidentId}
                  </strong>
                  {`${translate("global.successUpdateIncident")}`}
                </div>,
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => modalContext.setIsOpenModal(false)}
                >
                  {translate("global.accept")}
                </Button>,
                true
              );
              reset();
            }
            setIsActiveUpdate(!isActiveUpdate);
            setIsLoading(false);
          } else {
            modalContext.confirm(
              translate("global.alerts.ops"),
              translate("global.errorRegisterIncident"),
              <Button
                variant="outline"
                size="sm"
                onClick={() => modalContext.setIsOpenModal(false)}
              >
                {translate("global.accept")}
              </Button>,
              true
            );
            reset();
            
          setIsLoading(false);
          setIsActiveUpdate(!isActiveUpdate);
          }
        }

        // setIsActiveUpdate(!isActiveUpdate);
        // setIsLoading(false);
      }else{
        setIsLoading(false);}
    } catch (err) {
      modalContext.confirm(
        translate("global.alerts.ops"),
        translate(
          "dashboard.packages.loadpackages.formProvider.errorRegisterPackage"
        ),
        <Button
          variant="outline"
          size="sm"
          onClick={() => modalContext.setIsOpenModal(false)}
        >
          {translate("global.accept")}
        </Button>,
        true
      );
    } finally {
      setIsSendingData(false);
    }
  };

  const handleBack = () => history.push("/dashboard/incidents");

  // Si una validación falla, llevar la vista al campo: el técnico queda debajo del
  // pliegue y el error no se veía, así que «Guardar» parecía no hacer nada
  const scrollToField = (name) => {
    const field = document.querySelector(`#incident-detail-form [name="${name}"]`);
    const group = field?.closest(`.${styles.formGroup}`) ?? field;
    group?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleInvalidSubmit = (formErrors) => {
    setLocalErrors(formErrors);
    const [firstField] = Object.keys(formErrors);
    if (firstField) scrollToField(firstField);
  };

  // Locker y cliente se manejan en estado local (sus selects no se registran en el form)
  const handleStartEdit = () => {
    setEditPlatform(detailsIncidents?.IdPlataforma ?? null);
    setEditClient(detailsIncidents?.IdClientAssigned ?? null);
    setLocalErrors({});
    setIsActiveUpdate(false);
  };

  const renderDetailsIncident = (
    <form id="incident-detail-form" onSubmit={handleSubmit(submitUpdateIncident, handleInvalidSubmit)}>
      <div className={styles.detailsSection}>
        <div className={styles.sectionTitle}>
          <TbListDetails size={18} />
          {translate("global.incidentDetails")}
        </div>
        <div className={styles.checkboxChips}>
          <div className={styles.checkboxChip}>
            <input
              type="checkbox"
              disabled={isActiveUpdate}
              defaultChecked={detailsIncidents?.HasAction}
              name="actionIncident"
              id="actionCheck"
              ref={register()}
              onChange={(e) => {
                const checked = e.target.checked;
                const description = watch("description") || "";
                const actionTag = translate("global.listIncident.requiresAction");
                let newDescription = description;
                if (checked) {
                  if (!description.includes(actionTag)) {
                    newDescription = description.trim() + "\n" + actionTag;
                  }
                } else {
                  newDescription = description
                    .replace(new RegExp(`\\n?${actionTag}`, "g"), "")
                    .trim();
                }
                setValue("description", newDescription);
              }}
            />
            <label htmlFor="actionCheck">{translate("global.action")}</label>
          </div>
          <div className={styles.checkboxChip}>
            <input
              type="checkbox"
              ref={register()}
              disabled={isActiveUpdate}
              defaultChecked={detailsIncidents?.HasEscalar}
              id="escalateCheck"
              name="escalateIncident"
              onChange={(e) => {
                const checked = e.target.checked;
                const description = watch("description") || "";
                const escalateTag = translate("global.listIncident.escalated");
                let newDescription = description;
                if (checked) {
                  if (!description.includes(escalateTag)) {
                    newDescription = description.trim() + "\n" + escalateTag;
                  }
                } else {
                  newDescription = description
                    .replace(new RegExp(`\\n?${escalateTag}`, "g"), "")
                    .trim();
                }
                setValue("description", newDescription);
              }}
            />
            <label htmlFor="escalateCheck">{translate("global.escalate")}</label>
          </div>
        </div>

        <div
          className={`${styles.descriptionAlert} ${
            priorityToAlertClass[
              getColorPriorityIncident(detailsIncidents?.IdPriority)
            ] || styles.descriptionAlertPrimary
          }`}
          role="alert"
        >
          <div className={styles.descriptionAlertDate}>
            <LuCalendar size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
            <strong>{translate("global.dateCreated")}:</strong>{" "}
            {detailsIncidents?.FechaRegistro
              ? getFormatDate(detailsIncidents?.FechaRegistro, true)
              : ""}
          </div>
          <div className={styles.descriptionAlertText}>
            <strong>
              {getFlagPriorityIncident(detailsIncidents?.IdPriority)}{" "}
              {translate("global.description")}:{" "}
            </strong>
            {detailsIncidents?.Description || ""}
          </div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="incidentName" className={styles.formLabel}>
              <IoMdContact size={16} />
              {translate("global.name")}
            </label>
            <input
              type="text"
              id="incidentName"
              className={styles.formInput}
              name="nameClient"
              value={detailsIncidents?.NameClient || ""}
              disabled
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="incidentEmail" className={styles.formLabel}>
              <IoMailOutline size={16} />
              {translate("global.email")}
            </label>
            <input
              type="email"
              id="incidentEmail"
              className={styles.formInput}
              name="email"
              value={detailsIncidents?.Email || ""}
              disabled
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="incidentDate" className={styles.formLabel}>
              <LuCalendar size={16} />
              {translate("global.dateIncident")}
            </label>
            <input
              type="text"
              id="incidentDate"
              className={styles.formInput}
              name="dateIncident"
              value={
                detailsIncidents?.DateIncident
                  ? getFormatDate(detailsIncidents?.DateIncident)
                  : ""
              }
              disabled
            />
          </div>
        </div>

        <div className={styles.formGrid} style={{ marginTop: "2rem" }}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <IoSettingsOutline size={16} />
              {translate("global.typeIncident")}
            </label>
            {isActiveUpdate ? (
              <input
                type="text"
                defaultValue={getNameIncidentsTypes(detailsIncidents?.IdType)}
                disabled
                className={styles.formInput}
              />
            ) : (
              <TypeIncidentSelect
                errors={visibleErrors}
                register={register}
                setValue={setValue}
                nameShow={getNameIncidentsTypes(detailsIncidents?.IdType)}
                idShow={detailsIncidents?.IdType}
                sizeInput={"form-control-md"}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <TbWorld size={16} />
              {translate("global.origin")}
            </label>
            {isActiveUpdate ? (
              <input
                type="text"
                value={getNameIncidentsOrigins(detailsIncidents?.IdOrigin) || "—"}
                disabled
                className={styles.formInput}
              />
            ) : (
              <OriginIncidentList
                errors={visibleErrors}
                register={register}
                setValue={setValue}
                nameShow={getNameIncidentsOrigins(detailsIncidents?.IdOrigin) || undefined}
                idShow={detailsIncidents?.IdOrigin}
                isRequired={false}
                sizeInput={"form-control-md"}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <TbFlag3 size={16} />
              {translate("global.priority")}
            </label>
            {isActiveUpdate ? (
              <div className={styles.badgeWrapper}>
                <Badge
                  variant={
                    priorityToBadgeVariant[
                      getColorPriorityIncident(detailsIncidents?.IdPriority)
                    ] ?? "neutral"
                  }
                  size="sm"
                  icon={getFlagPriorityIncident(detailsIncidents?.IdPriority)}
                >
                  {getNameIncidentsPriority(detailsIncidents?.IdPriority)}
                </Badge>
              </div>
            ) : (
              <PriorityIncidentList
                errors={visibleErrors}
                register={register}
                setValue={setValue}
                nameShow={getNameIncidentsPriority(detailsIncidents?.IdPriority)}
                idShow={detailsIncidents?.IdPriority}
                sizeInput={"form-control-md"}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{translate("global.status")}</label>
            {isActiveUpdate ? (
              <div className={styles.badgeWrapper}>
                <Badge
                  variant={
                    statusColorToBadgeVariant[
                      getColorStatusIncident(detailsIncidents?.IdStatus)
                    ] ?? "neutral"
                }
                  size="sm"
                  icon={getIconStatusIncident(detailsIncidents?.IdStatus)}
                >
                  {getNameIncidentsStatus(detailsIncidents?.IdStatus)}
                </Badge>
              </div>
            ) : (
              <StatusIncidentList
                errors={visibleErrors}
                register={register}
                nameShow={getNameIncidentsStatus(detailsIncidents?.IdStatus)}
                idShow={detailsIncidents?.IdStatus}
                sizeInput={"form-control-md"}
                currentStatusId={detailsIncidents?.IdStatus}
              />
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <IoMdSettings size={14} />
              {translate("global.assignedTechnician")}
            </label>
            {isActiveUpdate ? (
              detailsIncidents?.NameUserAssigned ? (
                <AvatarName name={detailsIncidents?.NameUserAssigned} />
              ) : (
                <div className={styles.notAssignedChip}>
                  <span className={styles.notAssignedAvatar}>?</span>
                  {translate("global.notAssigned")}
                </div>
              )
            ) : (
              <AssignedUserSelect
                assignedUserId={assignedUserId}
                register={register}
                errors={visibleErrors}
                readOnly={isActiveUpdate}
                onChange={(val) => {
                  setAssignedUserId(val);
                  clearErrors("assignedUser");
                  setLocalErrors(({ assignedUser, ...rest }) => rest);
                }}
              />
            )}
          </div>
        </div>

        <div className={styles.formGridInfo} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formGroup} style={{ minWidth: 0 }}>
            <label className={styles.formLabel}>
              <PiLockers size={16} />
              {translate("global.locker")}
            </label>
            {isActiveUpdate ? (
              <div className={styles.formValueDisplay} title={detailsIncidents?.Plataforma || ""}>
                {detailsIncidents?.Plataforma || "—"}
              </div>
            ) : (
              <LockersSearchList
                errors={visibleErrors}
                fieldName="plataforma"
                name="plataforma"
                value={editPlatform ?? ""}
                placeholder={detailsIncidents?.Plataforma || translate("global.selectLocker")}
                isRequired={false}
                setSelectOption={(value) => setEditPlatform(value ?? null)}
              />
            )}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <FaDoorOpen size={16} />
              {translate("global.numDoor")}
            </label>
            {isActiveUpdate ? (
              <div className={styles.formValueDisplay}>
                {detailsIncidents?.DoorNumber ?? "—"}
              </div>
            ) : (
              <input
                type="text"
                className={styles.formInput}
                name="puerta"
                defaultValue={detailsIncidents?.DoorNumber ?? ""}
                ref={register()}
              />
            )}
          </div>
          <div className={`${styles.formGroup} ${styles.formGroupPackage}`}>
            <label className={styles.formLabel}>
              <PiPackageFill size={16} />
              {translate("global.packageId")}
            </label>
            {isActiveUpdate ? (
              <div className={styles.formValueDisplay}>
                {detailsIncidents?.IdPackage ?? "—"}
              </div>
            ) : (
              <input
                type="text"
                className={styles.formInput}
                name="idPackage"
                placeholder={translate("global.packageId.placeholder")}
                defaultValue={detailsIncidents?.IdPackage ?? ""}
                ref={register()}
              />
            )}
          </div>
        </div>

        <div className={styles.formGrid} style={{ marginTop: "1.5rem" }}>
          <div className={styles.formGroup} style={{ minWidth: 0, gridColumn: "1 / -1" }}>
            <label className={styles.formLabel}>
              <PiUser size={16} />
              {translate("global.client")}
            </label>
            {isActiveUpdate ? (
              <div className={styles.formValueDisplay} title={`${detailsIncidents?.IdClientAssigned || ""} - ${detailsIncidents?.NameClientAssigned || ""}`}>
                {detailsIncidents?.IdClientAssigned || detailsIncidents?.NameClientAssigned
                  ? `${detailsIncidents.IdClientAssigned || ""} - ${detailsIncidents.NameClientAssigned || ""}`.trim()
                  : "—"}
              </div>
            ) : (
              <ClientsSearchList
                errors={visibleErrors}
                allClients
                initialOption={
                  detailsIncidents?.IdClientAssigned
                    ? {
                        value: detailsIncidents.IdClientAssigned,
                        label: `${detailsIncidents.IdClientAssigned} - ${detailsIncidents?.NameClientAssigned || ""}`.trim(),
                      }
                    : null
                }
                setSelectOption={(value) => setEditClient(value ?? null)}
              />
            )}
          </div>
        </div>

        {!isActiveUpdate && (
          <div className={styles.formGroup} style={{ marginTop: "2rem" }}>
            <label htmlFor="incidentDescription" className={styles.formLabel}>
              <TbFileDescription size={16} />
              {translate("global.description")} <span style={{ color: "var(--color-error, #dc2626)" }}>*</span>
            </label>
            <textarea
              id="incidentDescription"
              rows={8}
              className={styles.formInput}
              name="description"
              ref={register()}
              required
              disabled={isActiveUpdate}
              style={{ resize: "vertical", minHeight: 180 }}
            />
          </div>
        )}
      </div>
    </form>
  );

  const handleGetIncident = async () => {
    setDetailsIncidents(null);
    const abortController = new AbortController();
    const response = await getDetailsIncident(id, abortController.signal);

    if (response?.filesImg) {
      response.filesImg = JSON.parse(response.filesImg);
    }else{
      response.filesImg = [];
    }    

    const filesUrl = response.filesImg.map((file) => file.split("/").pop());
    setFilesUrl(filesUrl);
    setDetailsIncidents(response);
  };

  const fetchSignedUrls = async () => {
    if (!filesUrl?.length) return;
    try {
      const abortController = new AbortController();
      const responses = await Promise.all(
        filesUrl.map((file) =>
          getViewUrlFile(abortController.signal, { fileKey: file.split("/").pop() })
        )
      );
      const urls = responses.map((r) => {
        if (typeof r === "string") return r;
        if (Array.isArray(r) && r.length > 0) {
          const first = r[0];
          return (typeof first === "string" ? first : first?.url) ?? null;
        }
        return null;
      });
      setSignedUrls(urls);
    } catch (error) {
      console.error("Error obteniendo URLs firmadas:", error);
      setSignedUrls(filesUrl.map(() => null));
    }
  };


  const handleGetTrackingIncident = async () => {
    const abortController = new AbortController();
    const response = await getTrackingIncident(id, abortController.signal);
    setTrackingIncidents(response);
  };

  useEffect(() => {
    handleGetTrackingIncident();
    handleGetIncident();
  }, [id]);

  useEffect(() => {
  if (detailsIncidents?.IdUserAssigned !== undefined) {
    setAssignedUserId(detailsIncidents.IdUserAssigned);
  }
}, [detailsIncidents]);

  useEffect(() => {
  // Solo si el componente no está en modo lectura Y SI EL STATUS DEL INCIDENTE ES 1
  if (!isActiveUpdate && assignedUserId && detailsIncidents?.IdStatus === 1) {
    setValue("statusIncident", 7); // ID 7 = Asignada
  }
}, [assignedUserId]);

  useEffect(() => {
    fetchSignedUrls();

    const intervalId = setInterval(fetchSignedUrls, 1200000);
    setFetchIntervalId(intervalId);

    return () => clearInterval(intervalId);
  }, [id, filesUrl]);



  const permissionsPath = "/dashboard/incidents";
  const canUpdate = verifyAccessViewPermissions(["update"], {
    path: permissionsPath || location.pathname,
  });
  return (
    <PageContainer>
      {detailsIncidents ? (
        <>
          <PageHeader
            title={`${translate("global.incident")} # ${id}`}
            breadcrumbs={[
              <span
                key="incidents"
                onClick={handleBack}
                className={styles.breadcrumbLink}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleBack();
                  }
                }}
              >
                {translate("global.listIncident")}
              </span>,
              `${translate("global.incident")} # ${id}`,
            ]}
            subtitle={
              <div className={styles.subtitleWithBadges}>
                <Badge
                  variant={
                    statusColorToBadgeVariant[
                      getColorStatusIncident(detailsIncidents?.IdStatus)
                    ] ?? "neutral"
                  }
                  size="sm"
                  icon={getIconStatusIncident(detailsIncidents?.IdStatus)}
                >
                  {getNameIncidentsStatus(detailsIncidents?.IdStatus)}
                </Badge>
                <Badge
                  variant={
                    priorityToBadgeVariant[
                      getColorPriorityIncident(detailsIncidents?.IdPriority)
                    ] ?? "neutral"
                  }
                  size="sm"
                  icon={getFlagPriorityIncident(detailsIncidents?.IdPriority)}
                >
                  {getNameIncidentsPriority(detailsIncidents?.IdPriority)}
                </Badge>
                <span style={{ fontSize: "var(--font-size-sm)", color: "var(--text-secondary)" }}>
                  {getNameIncidentsTypes(detailsIncidents?.IdType)}
                </span>
              </div>
            }
            icon={<LuAlertCircle size={28} />}
            actions={
              <div className={styles.headerActions}>
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={<FaChevronLeft size={14} />}
                  onClick={handleBack}
                >
                  {translate("global.return")}
                </Button>
                {canUpdate &&
                  (isActiveUpdate ? (
                    <Button
                      variant="primary"
                      size="md"
                      leftIcon={isLoading ? <Spinner type={1} size={14} /> : <MdEdit size={18} />}
                      disabled={isLoading}
                      onClick={handleStartEdit}
                    >
                      {translate("global.edit")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        form="incident-detail-form"
                        variant="primary"
                        size="md"
                        leftIcon={<IoIosSave size={18} />}
                        disabled={isSendingData}
                      >
                        {translate("global.save")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => setIsActiveUpdate(true)}
                      >
                        {translate("global.cancel")}
                      </Button>
                    </>
                  ))}
              </div>
            }
            divider={false}
          />

          <div className={styles.twoColumnLayout}>
            <div className={styles.mainContent}>
              <ContentCard padding="md">
                {renderDetailsIncident}
                {filesUrl.length > 0 && (
                  <div className={styles.attachmentsSection}>
                    <h6 className={styles.attachmentsTitle}>
                      {translate("global.attachments")}
                    </h6>
                    <FileViewerAWS signedUrls={signedUrls} fileKeys={filesUrl} />
                  </div>
                )}
              </ContentCard>
            </div>
            <div className={styles.sidebarContent}>
              <ShowTracking
                itemArray={trackingIncidents}
                title={translate("global.incidentTracking")}
                Item={(props) => <ItemIncidents {...props} />}
                noItemsFound={translate("dashboard.delivery.table.noItemsFound")}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <PageHeader
            title={`${translate("global.incident")} # ${id}`}
            icon={<LuAlertCircle size={28} />}
            divider={false}
          />
          <div className={styles.loadingContainer}>
            <Spinner type={1} size={40} />
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default UpdateIncident;
